<template>
  <div class="flex flex-1 flex-col gap-4">
    <!-- 基础信息：头像 / 用户名 / 邮箱 / 角色 -->
    <div
      class="relative overflow-hidden rounded-2xl border border-primary-100/80 dark:border-primary-900/40 bg-gradient-to-br from-primary-50 via-white to-white dark:from-primary-900/25 dark:via-zinc-900 dark:to-zinc-900 p-5"
    >
      <div class="pointer-events-none absolute -top-12 -right-10 w-36 h-36 rounded-full bg-primary/10 blur-2xl" aria-hidden="true" />
      <div class="relative flex items-center gap-4">
        <UserAvatar :src="avatar" :size="80" />
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <span class="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">{{ username || '—' }}</span>
            <a-tag :color="isAdmin ? 'green' : 'default'" class="!mr-0">
              {{ isAdmin ? t('shell.account.roleAdmin') : t('shell.account.roleUser') }}
            </a-tag>
          </div>
          <div class="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            <Mail class="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            <span class="truncate">{{ email || t('shell.account.emailUnset') }}</span>
          </div>
          <div
            v-if="phoneEnabled"
            class="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400"
          >
            <Phone class="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            <span class="truncate">{{ phone || t('shell.account.phoneUnset') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 点数：余额 + 累计充值 / 消耗 + 去充值（开启点数功能时展示） -->
    <div v-if="showCredit" class="rounded-2xl border border-gray-100/80 dark:border-gray-700/50 bg-surface/60 dark:bg-panel/30 p-5">
      <a-spin :spinning="creditLoading">
        <div class="flex items-end justify-between gap-3">
          <div>
            <div class="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
              <Coins class="w-4 h-4 text-primary" aria-hidden="true" />
              {{ t('shell.account.balance') }}
            </div>
            <div class="mt-1 flex items-baseline gap-1.5">
              <span class="text-3xl font-bold text-primary tabular-nums">{{ credit.toLocaleString() }}</span>
              <span class="text-xs text-gray-400">{{ t('shell.account.pointsUnit') }}</span>
            </div>
          </div>
          <a-button type="primary" @click="emit('navigate', 'recharge')">
            <div class="inline-flex items-center gap-1">
              <Plus class="w-4 h-4" aria-hidden="true" />
              {{ t('shell.account.goRecharge') }}
            </div>
          </a-button>
        </div>
        <div class="mt-4 grid grid-cols-2 gap-3">
          <div class="rounded-xl bg-gray-50 dark:bg-zinc-800/40 px-3 py-2">
            <div class="text-xs text-gray-400">{{ t('shell.account.totalPurchased') }}</div>
            <div class="text-base font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{{ totalPurchased.toLocaleString() }}</div>
          </div>
          <div class="rounded-xl bg-gray-50 dark:bg-zinc-800/40 px-3 py-2">
            <div class="text-xs text-gray-400">{{ t('shell.account.totalConsumed') }}</div>
            <div class="text-base font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{{ totalConsumed.toLocaleString() }}</div>
          </div>
        </div>
      </a-spin>
    </div>

    <!-- 退出登录：贴内容区底部，与面板最小高度配合 -->
    <div class="mt-auto pt-1">
      <a-button danger block @click="emit('logout')">
        <div class="inline-flex items-center gap-1">
          <LogOut class="w-4 h-4" aria-hidden="true" />
          {{ t('shell.logout') }}
        </div>
      </a-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Coins, LogOut, Mail, Phone, Plus } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'
import { getMyCredit } from '../../api/user/recharge'
import { useI18n } from '../../composables/useI18n'
import { useAuthStore } from '../../stores/auth'
import UserAvatar from '../UserAvatar.vue'

const props = withDefaults(
  defineProps<{
    username?: string
    email?: string
    phone?: string
    avatar?: string
    showCredit?: boolean
    /** 后台是否开启手机号相关功能，关闭时不展示手机号 */
    phoneEnabled?: boolean
  }>(),
  { username: '', email: '', phone: '', avatar: '', showCredit: false, phoneEnabled: false },
)
const emit = defineEmits<{ navigate: [key: string]; logout: [] }>()

const { t } = useI18n()
const auth = useAuthStore()

const credit = ref(0)
const totalPurchased = ref(0)
const totalConsumed = ref(0)
const creditLoading = ref(true)

const isAdmin = computed(() => auth.isAdmin)

onMounted(async () => {
  if (!props.showCredit) {
    creditLoading.value = false
    return
  }
  try {
    const info = await getMyCredit()
    credit.value = info.credit || 0
    totalPurchased.value = info.totalPurchased || 0
    totalConsumed.value = info.totalConsumed || 0
  } catch {
    // ignore
  } finally {
    creditLoading.value = false
  }
})
</script>
